/**
 * Formatters for Fulfillment Lifecycle Dashboard
 *
 * Utility functions for formatting route data display values.
 */

/**
 * Format aging time in minutes to human readable format
 * @param {number|null} minutes
 * @returns {string|null}
 */
export const formatAgingTime = (minutes) => {
    if (minutes === null || minutes === undefined) {
        return null;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

/**
 * Format pull time from HH:MM:SS to HH:MM
 * @param {string|null} pullTime
 * @returns {string|null}
 */
export const formatPullTime = (pullTime) => {
    if (!pullTime) {
        return null;
    }
    // Handle both HH:MM:SS and HH:MM formats
    return pullTime.slice(0, 5);
};

/**
 * Generate route identifier string
 * @param {Object} route
 * @param {string} route.pullTime
 * @param {string} route.carrier
 * @param {string} route.marketCode
 * @returns {string}
 */
export const getRouteName = (route) => {
    const pullTime = route.pullTime || '';
    const carrier = route.carrier || '';
    const marketCode = route.marketCode || '';
    return `${pullTime}_${carrier}_${marketCode}`;
};

/**
 * Get status message for route card
 * @param {Object} route
 * @param {number} [route.behindScheduleMinutes] - Minutes behind schedule
 * @param {number} route.transitCount - Items in transit
 * @param {number} route.totalShipments - Total shipments
 * @param {number|null} [pullTimeAgoMinutes] - Minutes since pull time (computed externally)
 * @returns {string|null}
 */
export const getStatusMessage = (route, pullTimeAgoMinutes = null) => {
    // Prioritize behind schedule message
    if (route.behindScheduleMinutes && route.behindScheduleMinutes > 0) {
        return `~${route.behindScheduleMinutes}m behind schedule`;
    }

    // Show pull time ago with not loaded count
    if (pullTimeAgoMinutes && pullTimeAgoMinutes > 0) {
        const notLoaded = route.totalShipments - route.transitCount;
        if (notLoaded > 0) {
            return `Pull ${pullTimeAgoMinutes}m ago, ${notLoaded} not loaded`;
        }
    }

    return null;
};

/**
 * Calculate delta between pull time and now in minutes
 * @param {string|null} pullTime - HH:MM:SS format
 * @param {Date} now - Current time
 * @returns {number|null} - Positive if pull time is past, negative if future
 */
export const getPullTimeDelta = (pullTime, now) => {
    if (!pullTime) {
        return null;
    }

    const [hours, minutes] = pullTime.split(':').map(Number);
    const pullDate = new Date(now);
    pullDate.setHours(hours, minutes, 0, 0);

    const deltaMs = now.getTime() - pullDate.getTime();
    return Math.round(deltaMs / (1000 * 60));
};
